import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { ToastService } from './shared/toast/toast.service';

interface LambdaErrorResponse {
    errorType?: string;
    errorMessage?: string;
    statusCode?: number;
    message?: string;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const toastService = inject(ToastService);
    const skipToast = req.headers.has('X-Skip-Error-Toast');
    const isThirdPartyRequest = req.body && typeof req.body === 'object' && 
                                (req.body as any).requestType === 'getPolicySnapshotForTP';

    return next(req).pipe(
        map((event) => {
            if (event instanceof HttpResponse && (event.body as LambdaErrorResponse)?.errorType) {
                const errorBody = event.body as LambdaErrorResponse;
                let statusCode = 200;
                
                // Check if it's a validation error
                if (errorBody.errorMessage?.includes('validation failed') || 
                    errorBody.errorMessage?.includes('mismatch')) {
                    statusCode = 403;
                }
                
                throw new HttpErrorResponse({
                    error: event.body,
                    status: statusCode,
                    statusText: errorBody.errorMessage,
                });
            }
            if (event instanceof HttpResponse && (event.body as LambdaErrorResponse)?.statusCode === 401) {
                throw new HttpErrorResponse({
                    error: event.body,
                    status: 401,
                    statusText: (event.body as LambdaErrorResponse)?.message || 'Unauthorized',
                });
            }
            if (event instanceof HttpResponse && (event.body as LambdaErrorResponse)?.statusCode === 403) {
                throw new HttpErrorResponse({
                    error: event.body,
                    status: 403,
                    statusText: (event.body as LambdaErrorResponse)?.message || 'Forbidden',
                });
            }
            return event;
        }),
        catchError((error: HttpErrorResponse) => {
            if (!skipToast) {
                let title = 'API Request Failed';
                let message = 'Something went wrong. Please try again later.';

                if (error.status === 0) {
                    message = 'Unable to connect to the server. Please check your internet connection.';
                } else if (error.status === 200) {
                    if (isThirdPartyRequest && error.error?.errorMessage?.includes('Session not found')) {
                        title = 'Session Error';
                        message = 'Session not found or has expired. Please use a valid link.';
                    } else {
                        message = error.error?.errorMessage || 'The request could not be processed.';
                    }
                } else if (error.status === 401) {
                    if (isThirdPartyRequest) {
                        title = 'Unauthorized';
                        message = error.error?.message || 'Session has expired or is invalid. Please try again.';
                    } else {
                        title = 'Request Error';
                        message = error.error?.message || 'The request could not be processed.';
                    }
                } else if (error.status === 403) {
                    if (isThirdPartyRequest) {
                        title = 'Authentication Failed';
                        message = error.error?.errorMessage || error.error?.message || 'The information provided does not match our records. Please verify and try again.';
                    } else {
                        title = 'Forbidden';
                        message = error.error?.message || 'You do not have permission to access this resource.';
                    }
                } else if (error.status >= 400 && error.status < 500) {
                    title = 'Request Error';
                    message = error.error?.message || 'The request could not be processed.';
                } else if (error.status >= 500) {
                    title = 'Server Error';
                    message = 'The server encountered an error. Please try again later.';
                }

                toastService.add({ type: 'error', title, message });
            }

            return throwError(() => error);
        })
    );
};
